import styles from 'css/src/components/common/ui/Callout.module.scss';

import React from 'react';
import classnames from 'classnames';

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    BUTTON_WIDTHS,
    SimpleButton,
} from 'js/src/components/common/ui/Button';

export enum CALLOUT_TYPES {
    PROMO = 'promo',
    UPSELL = 'upsell',
    INFO = 'info',
    ERROR = 'error',
    ALERT = 'alert',
}

type CalloutProps = {
    msgType?: CALLOUT_TYPES;
    title?: React.ReactNode;
    text?: React.ReactNode;
    ctaText?: string;
    onCtaClick?: (event?: React.MouseEvent) => void;
    dismissHandler?: (event?: React.MouseEvent) => void;
    dismissable?: boolean;
    visible?: boolean;
    className?: string;
    'data-testid'?: string;
};

const CTA_BUTTON_CONFIG: Record<CALLOUT_TYPES, { color: BUTTON_COLORS; variant: BUTTON_VARIANTS }> =
    {
        [CALLOUT_TYPES.PROMO]: { color: BUTTON_COLORS.PRIMARY, variant: BUTTON_VARIANTS.FILLED },
        [CALLOUT_TYPES.UPSELL]: { color: BUTTON_COLORS.UPSELL, variant: BUTTON_VARIANTS.FILLED },
        [CALLOUT_TYPES.INFO]: { color: BUTTON_COLORS.PRIMARY, variant: BUTTON_VARIANTS.OUTLINE },
        [CALLOUT_TYPES.ERROR]: { color: BUTTON_COLORS.DANGER, variant: BUTTON_VARIANTS.OUTLINE },
        [CALLOUT_TYPES.ALERT]: { color: BUTTON_COLORS.TERTIARY, variant: BUTTON_VARIANTS.OUTLINE },
    };

const Callout: React.FC<CalloutProps> = React.memo(function Callout({
    msgType = CALLOUT_TYPES.INFO,
    title,
    text,
    ctaText,
    onCtaClick,
    dismissHandler,
    dismissable = false,
    visible = true,
    className = '',
    'data-testid': dataTestId,
}) {
    const [showing, setShowing] = React.useState(visible);
    const onDismiss = React.useCallback(
        (e: React.MouseEvent) => {
            setShowing(false);
            if (dismissHandler) {
                dismissHandler(e);
            }
        },
        [dismissHandler],
    );

    if (!showing) {
        return null;
    }

    const ctaConfig = CTA_BUTTON_CONFIG[msgType];

    return (
        <div
            className={classnames(className, styles['callout'], {
                [styles['callout-promo']]: msgType === CALLOUT_TYPES.PROMO,
                [styles['callout-upsell']]: msgType === CALLOUT_TYPES.UPSELL,
                [styles['callout-info']]: msgType === CALLOUT_TYPES.INFO,
                [styles['callout-error']]: msgType === CALLOUT_TYPES.ERROR,
                [styles['callout-alert']]: msgType === CALLOUT_TYPES.ALERT,
            })}
            role="alert"
            data-testid={dataTestId}
        >
            <div className={styles['callout-content']}>
                {title && <p className={styles['callout-title']}>{title}</p>}
                {text && <p className={styles['callout-text']}>{text}</p>}
            </div>
            {ctaText && onCtaClick && (
                <Button
                    text={ctaText}
                    color={ctaConfig.color}
                    variant={ctaConfig.variant}
                    size={BUTTON_SIZES.SM}
                    width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                    className="flex-shrink-0"
                    onClick={onCtaClick}
                />
            )}
            {dismissable && (
                <SimpleButton
                    width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                    size={BUTTON_SIZES.SM}
                    className="btn-close flex-shrink-0"
                    aria-label="Close"
                    onClick={onDismiss}
                />
            )}
        </div>
    );
});

export default Callout;
